import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { IconButton, Popover } from '@mui/material';
import { func, string } from 'prop-types';
import React, { memo, useEffect, useRef, useState } from 'react';

const loadPicker = () => import('./Picker').then(m => ({ Picker: m.default, data: m.data }));

let cached = null;

const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 435;

const I18N_EU = {
  search: 'Bilatu',
  search_no_results_1: 'Aizu!',
  search_no_results_2: 'Ezin izan da emoji hori aurkitu',
  pick: 'Aukeratu emoji bat…',
  add_custom: 'Emoji pertsonalizatua gehitu',
  categories: {
    activity: 'Jarduerak',
    custom: 'Pertsonalizatuak',
    flags: 'Banderak',
    foods: 'Janaria eta Edaria',
    frequent: 'Maiz erabilitakoak',
    nature: 'Animaliak eta Natura',
    objects: 'Objektuak',
    people: 'Aurpegierak eta Pertsonak',
    places: 'Bidaiak eta Lekuak',
    search: 'Bilaketaren emaitzak',
    symbols: 'Sinboloak',
  },
  skins: {
    choose: 'Aukeratu azal-tonu lehenetsia',
    1: 'Lehenetsia',
    2: 'Argia',
    3: 'Argi-ertaina',
    4: 'Ertaina',
    5: 'Erdi-iluna',
    6: 'Iluna',
  },
};

const I18N_CA = {
  search: 'Cerca',
  search_no_results_1: 'Oh no!',
  search_no_results_2: "No s'ha pogut trobar aquest emoji",
  pick: 'Tria un emoji…',
  add_custom: 'Afegir emoji personalitzat',
  categories: {
    activity: 'Activitats',
    custom: 'Personalitzats',
    flags: 'Banderes',
    foods: 'Menjar i Beguda',
    frequent: "D'ús freqüent",
    nature: 'Animals i Natura',
    objects: 'Objectes',
    people: 'Emoticones i Persones',
    places: 'Viatges i Llocs',
    search: 'Resultats de la cerca',
    symbols: 'Símbols',
  },
  skins: {
    choose: 'Tria el to de pell predeterminat',
    1: 'Per defecte',
    2: 'Clar',
    3: 'Mig-clar',
    4: 'Mitjà',
    5: 'Mig-fosc',
    6: 'Fosc',
  },
};

const CUSTOM_I18N = { eu: I18N_EU, 'es-ca': I18N_CA, ca: I18N_CA };

const PickEmojiButton = ({ onPick, onOpen, onClose: onCloseProp, locale }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerData, setPickerData] = useState(() => cached);
  const pendingAnchor = useRef(null);
  const open = Boolean(anchorEl) && Boolean(pickerData);

  useEffect(() => {
    const preload = () => {
      if (cached) return;
      loadPicker().then(({ Picker: _P, data: d }) => {
        cached = { Picker: _P, data: d };
        setPickerData(cached);
      });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(preload, { timeout: 1000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 500);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (pickerData && pendingAnchor.current) {
      setAnchorEl(pendingAnchor.current);
      pendingAnchor.current = null;
    }
  }, [pickerData]);

  const onButtonClick = event => {
    if (onOpen) onOpen();
    const target = event.currentTarget;
    if (cached) {
      if (!pickerData) setPickerData(cached);
      setAnchorEl(target);
    } else {
      pendingAnchor.current = target;
      loadPicker().then(({ Picker: P, data: d }) => {
        cached = { Picker: P, data: d };
        setPickerData(cached);
      });
    }
  };

  const onClose = () => {
    setAnchorEl(null);
    if (onCloseProp) onCloseProp();
  };
  const onEmojiSelect = emoji => {
    if (emoji?.native) onPick(emoji.native);
  };

  const PickerComponent = pickerData?.Picker;

  return (
    <>
      <IconButton size="small" onClick={onButtonClick}>
        <InsertEmoticonIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: { width: PICKER_WIDTH, height: PICKER_HEIGHT, overflow: 'hidden' },
          },
        }}
      >
        {PickerComponent && pickerData?.data ? (
          <PickerComponent
            data={pickerData.data}
            onEmojiSelect={onEmojiSelect}
            {...(CUSTOM_I18N[locale] ? { i18n: CUSTOM_I18N[locale] } : { locale })}
            previewPosition="none"
            skinTonePosition="none"
          />
        ) : null}
      </Popover>
    </>
  );
};

PickEmojiButton.propTypes = {
  onPick: func.isRequired,
  onOpen: func,
  onClose: func,
  locale: string,
};

export default memo(PickEmojiButton);
